// Modele de donnees de la vue acheteur operation-bc (v2).
// Source unique des codes de statut : table `status` (bdc/api seeder).
// Fin des magic numbers disperses dans l'ancienne infra.

export enum StatusId {
  BrouillonAcheteur = 1,
  BrouillonPrestataire = 2,
  AttenteAcceptationRang1 = 3,
  AAccepter = 4,
  AttenteAcceptationRang2 = 5,
  AttenteAcceptationRang3 = 6,
  Valide = 7, // Accepte / Valide
  Refuse = 8,
  Expire = 9,
  AttenteOffre = 10,
  AChiffrer = 11,
  AAnalyser = 12,
  AttenteAnalyse = 13,
  Annule = 14,
  PropositionFaite = 15, // En attente de decision de l'acheteur
}

export type Rang = 1 | 2 | 3;

export type TabKind =
  | 'historique'
  | 'consultation'
  | 'offre'
  | 'notification'
  | 'messagerie'
  | 'evaluation';

// Un onglet est une donnee, pas un libelle a re-parser ni un index a deviner.
export interface TabDescriptor {
  key: string;
  kind: TabKind;
  rang?: Rang;
  label: string;
  disabled: boolean;
  inactive: boolean;
}

export interface StatusRef {
  id?: number;
  nom?: string;
}

// Article du catalogue tel que renvoye par l'API (porte par une commande de rang).
export interface CatalogueArticleData {
  id?: number | string;
  quantity?: number | string;
  quantite?: number | string;
  prix_unitaire?: number | string;
  prix?: number | string;
  prix_total?: number | string;
  totalHT?: number | string;
}

export interface CatalogueItem {
  id?: number | string;
  code?: number | string;
  nom?: string;
  description?: string;
  unite?: string;
  seuil_a?: number;
  seuil_b?: number;
  article?: CatalogueArticleData;
}

export interface CommandeArticle {
  catalogue?: CatalogueItem;
}

// Evenement d'historique (commande ou operation).
export interface HistoriqueEvent {
  prefix?: string;
  type?: string;
  label?: string | null;
  // Auteur de l'action tel qu'inscrit par le core sur l'evenement (colonne `operateur`) : la personne
  // qui a lance (prenom nom), avec repli organisme cote BdC. Source du recap « lancee par … » (parite
  // MS getLancementConsultationInfo).
  operateur?: string | null;
  // Cible de l'evenement (colonne `destinataire`). Pour un evenement 'Consultation', le core y met
  // l'id de la commande du prestataire consulte pour ce rang -> permet de rattacher l'evenement au
  // bon rang en BC multi-rang. Anciens evenements de lancement : 'systeme'.
  destinataire?: string | number | null;
  message?: string;
  type_compte?: string;
  created_at?: string;
  updated_at?: string;
}

export interface EntiteRef {
  nom?: string;
  siret?: string;
}

export interface ContratRef {
  /** Numero du contrat accord-cadre du titulaire (ex : "25-423"). */
  numero?: string;
  entite?: EntiteRef;
}

export interface LotTechnique {
  id?: number | string;
  nom?: string;
}

export interface LotDepartement {
  departement?: { id?: number | string; nom?: string };
}

export interface OrganismeRef {
  id?: number;
  nom?: string;
  // Enrichis par le gateway core (BdCController::showById) : organisme complet + adresse.
  siret?: string;
  type_organisme?: string;
  adresse?: {
    numero_voie?: string;
    type_voie?: string;
    nom_voie?: string;
    code_postal?: string;
    ville?: string;
    pays?: string;
  };
}

// Acheteur signataire / validateur (cible d'une demande de validation).
export interface Signataire {
  id: number;
  type: string;
  prenom?: string;
  nom?: string;
  fonction?: string;
  email_contact?: string;
}

// Sac de formulaire (cote front) : champs connus + reste libre (saisie utilisateur).
export interface OperationFormulaire {
  referenceInterne?: string;
  nomOperation?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  coordonneesMaitrise?: string;
  delaisConsultation?: number | string;
  delaisFinOperation?: number | string;
  visitePreavis?: boolean | number | string;
  conditionVisite?: string;
  dateDebutConsultation?: string;
  dateFinConsultation?: string;
  dateDebutOperation?: string;
  dateFinOperation?: string;
  besoin?: string;
  // Descriptif refonte (parite MS), saisis par l'acheteur (camelCase cote form).
  nomOuvrage?: string;
  pays?: string;
  moeInterlocuteur?: string;
  moeMail?: string;
  moeTelephone?: string;
  niveauSensibilite?: string;
  signataire?: string;
  phases?: string;
  verificationIdentites?: string;
  habilitation?: string;
  charteReglementation?: string;
  clauseSuiviEnvironnemental?: string;
  modaliteFacturation?: string;
  // Contact operationnel de CETTE operation (saisi, pre-rempli avec l'acheteur qui l'initie).
  contactNom?: string;
  contactFonction?: string;
  contactMail?: string;
  contactTelephone?: string;
  urlDocTechnique?: string;
  modaliteAccesDocumentation?: string;
  listePiecesDocumentation?: string;
  [key: string]: unknown;
}

export interface CommandeRang {
  id?: number;
  rang: string | number;
  id_status: number;
  /** Pivot `lot_contrat` (lot x contrat x rang) : c'est lui qui resout le titulaire et son contrat. */
  id_contrat?: number;
  articles?: CommandeArticle[];
  historique?: HistoriqueEvent[];
  contrat?: ContratRef;
  type_compte?: string;
  // Offre technique SAISIE par le prestataire sur SA commande (par rang). L'acheteur la consulte
  // ici en lecture seule ; nullable tant que le presta n'a rien renseigne (brouillon).
  offre_technique?: string | null;
  updated_at?: string;
  // Entite API large : champs supplementaires accessibles en `unknown` (a declarer si utilises).
  [key: string]: unknown;
}

// Entite operation-bc telle que consommee par la vue acheteur. Les champs sont
// declares explicitement ; l'index signature reste un filet `unknown` (jamais `any`)
// pour les attributs API non encore typises.
export interface OperationBC {
  id: number;
  numero?: string | number;
  nom?: string;
  reference?: string;
  besoin?: string;
  adresse?: string;
  code_postal?: string;
  ville?: string;
  facturation?: string;
  coordonnees_maitrise_ouvrage?: string;
  coordonnees_maitrise?: string;
  consultation?: number | string;
  delai_execution?: number | string;
  delais?: number | string;
  visite?: boolean | number | string;
  condition_visite?: string;
  date_debut_operation?: string;
  date_fin_operation?: string;
  // Descriptif refonte (parite MS) : colonnes ajoutees sur bdc.operation.
  nom_ouvrage?: string;
  // Contact operationnel porte par l'OPERATION (colonnes contact_* ajoutees en base), distinct du
  // compte acheteur : deux operations du meme acheteur peuvent designer deux interlocuteurs.
  contact_nom?: string;
  contact_fonction?: string;
  contact_mail?: string;
  contact_telephone?: string;
  pays?: string;
  moe_interlocuteur?: string;
  moe_mail?: string;
  moe_telephone?: string;
  niveau_sensibilite?: string;
  signataire_commande?: string;
  phases?: string;
  verification_identites?: string;
  habilitation?: string;
  charte_reglementation?: string;
  clause_suivi_environnemental?: string;
  url_doc_technique?: string;
  modalite_acces_documentation?: string;
  liste_pieces_documentation?: string;
  id_status?: number;
  status?: StatusRef;
  rang_actuel?: number;
  transferred?: boolean;
  commandes?: CommandeRang[];
  historique?: HistoriqueEvent[];
  lot_technique?: LotTechnique;
  id_lot_departement?: number;
  lot_departement?: LotDepartement;
  organisme?: OrganismeRef;
  acheteur?: { prenom?: string; nom?: string; email_contact?: string; telephone?: string; fonction?: string };
  prestataire?: { nom?: string };
  operation?: OperationBC; // certaines reponses imbriquent l'operation
  expire_at?: string;
  organisme_nom?: string;
  created_at?: string;
  updated_at?: string;
  formulaire?: OperationFormulaire;
  [key: string]: unknown;
}
